"use client";

/**
 * ShowcaseRowV3 — the four-door showcase ("Watch it / Ask it / Hear it /
 * Call it"), rebuilt in round 4 as a proper HORIZONTAL ACCORDION.
 *
 * Why the rebuild (Jay's round-4 review): the previous version weighted the
 * panes by flex-grow on hover. It had two problems Jay called out — collapsed
 * panes truncated their titles to a single letter ("W…", "He…"), and the
 * expanded video read as a small floating box rather than filling its pane.
 * This is the standard accordion pattern instead: exactly ONE pane is
 * expanded at any time, the other three are fixed-width strips whose full
 * title renders vertically (never truncated), and the expanded pane's content
 * (video, chat, episode list, phone number) fills it edge to edge.
 *
 * Interaction (desktop ≥900px):
 *   - Exactly one pane open; default is the video.
 *   - Hover a collapsed strip → it expands, the previous one collapses.
 *   - Also click and keyboard: each pane's strip is a real <button> carrying
 *     aria-expanded, with a visible focus outline. Focusing a strip (Tab) or
 *     clicking/Enter/Space opens it.
 *   - Smooth ~.45s width transition (flex-grow interpolation — the collapsed
 *     panes hold a fixed 64px basis, only grow animates). Under
 *     prefers-reduced-motion the swap is instant, no animation.
 *
 * Interaction (mobile <900px): vertical accordion — four stacked header bars,
 * exactly one open (video default), tap a header to open it and collapse the
 * others. No opening choreography on mobile.
 *
 * Opening choreography: on load (desktop, motion allowed only) the open pane
 * cycles video → ask → hear → call, ~1s each, then settles back on the video.
 * It's a setTimeout state machine, not a CSS animation, so any real
 * interaction (hover / click / touch / focus anywhere in the row) cancels it
 * permanently and immediately. See useAccordionChoreography below.
 *
 * The pane's trigger is a separate <button> from the pane's body on purpose:
 * the bodies contain their own interactive elements (video controls, the
 * tel: link, the copy-to-clipboard chips), and nesting interactive content
 * inside a <button> is invalid HTML. So the button is the strip/header, and
 * the body sits beside it — reachable only when the pane is expanded.
 *
 * Door honesty (per Jay + j-critic, carried over from /v2): all four doors are
 * real product now —
 *   - Watch it → the real trailer <video>, in-page only (no download,
 *                new-tab, or picture-in-picture — see props on the tag),
 *                with a click-to-play button so it doesn't read as a still.
 *   - Ask it   → a REAL conversation as of Round 7: questions post to
 *                /api/fideo-ask and come back computed from Rachel's actual
 *                numbers. It was a hardcoded mock before, which is the one
 *                door that wasn't honest.
 *   - Hear it  → the real audio edition of the trailer (<audio>, the losslessly
 *                extracted 3:05 AAC track (184.95s of audio inside the 3:10
 *                v7 container — the trailing silent end card is video-only),
 *                preload="none" so it costs nothing
 *                until pressed).
 *   - Call it  → the real tel: link, tap-to-dial (on the number, once open).
 */

import { useEffect, useRef, useState } from "react";
import Script from "next/script";
import posthog from "posthog-js";
import { ASK_CHIPS, CALENDLY_URL } from "./content";
import { RACHEL_AUDIO_PEAKS } from "./rachelAudioPeaks";
import { useMediaTracking } from "@/components/blog/TrackedMedia";

type PaneKey = "video" | "ask" | "podcast" | "phone";

/**
 * Everything that differs between the homepage demo (Rachel) and a blog
 * note. The homepage passes nothing and gets DEFAULT_CONFIG (byte-identical
 * to the Round 7 row); a note passes its own video, run and audio, with
 * `phone: false` because the phone line is a homepage promise, not a note.
 */
export interface ShowcaseConfig {
  video: { src: string; poster: string; kicker: string; caption: string; ariaLabel: string };
  ask: {
    runId: string;
    title: string;
    topic: string;
    chips: string[];
    emptyCopy: string;
    placeholder: string;
    simNote?: string;
    /** Set for blog notes: counts asks + surfaces most-asked chips. */
    noteSlug?: string;
  };
  hear: { src: string; peaks: number[]; title: string; kicker: string; line: string; simNote?: string };
  phone: boolean;
  /** Set for blog notes: PostHog play/progress events on the video + audio. */
  track?: { slug: string; title: string };
}

// ── ElevenLabs ask widget (ported verbatim from app/v2/V2Client.tsx, which is
// never modified).
//
// Round 7 mount change: it used to mount as soon as a real user expanded the
// "Ask it" pane, which meant a floating orb appeared bottom-right unannounced
// — Jay saw it and reasonably asked what it was. The embed renders itself
// fixed to the viewport corner from inside its own shadow DOM, so it CANNOT
// be docked into the pane; CSS on our side does not move it (see the dead
// rule this replaced in ../shared.tsx). So it is now behind an explicit
// "Talk to it instead" button in the Ask it pane. Nothing floats unless the
// visitor asked for voice. Text Q&A is the pane's default and is fully
// in-pane — see <AskItChat /> below.
const ASK_AGENT_ID = "agent_3501kxtgtd64ftbbznasq35f74vq";
const ASK_DEMO_RUN = {
  runId: "rachel-portfolio-demo-v1",
  title: "Rachel's Quarterly Portfolio Review",
  topic:
    "Simulated demo portfolio — defensive book, Q1 selloff, Q2 recovery, $2,181,000 total",
};

// Text Q&A limits, mirroring the proxy route's own guards
// (app/api/fideo-ask/route.ts) so the pane rejects locally rather than
// spending a backend call on something the route will 400 anyway.
const MAX_QUESTION_LEN = 280;
// Cache hits come back in ~100–200ms, which reads as canned rather than
// computed. Holding the thinking state to this floor is the same trick
// AskThisVideo uses on the fideo page.
const MIN_THINK_MS = 2000;

type ChatRole = "user" | "assistant";
interface ChatMessage {
  role: ChatRole;
  content: string;
  rateLimited?: boolean;
}

// Splits **bold** spans out of backend answers. No other markdown is
// interpreted and we never use dangerouslySetInnerHTML, so arbitrary backend
// text renders as plain text.
function renderAnswer(text: string) {
  return text.split(/(\*\*[^*]+\*\*)/g).map((part, i) =>
    part.startsWith("**") && part.endsWith("**") ? (
      <strong key={i}>{part.slice(2, -2)}</strong>
    ) : (
      <span key={i}>{part}</span>
    )
  );
}

/**
 * The "Ask it" pane's real conversation.
 *
 * Round 7: this pane used to be a hardcoded mock — two static bubbles and
 * chips that copied the question to your clipboard. The genuinely working
 * chat lived only on the /fideo page (components/fideo/AskThisVideo.tsx),
 * and the homepage lost it when Version A was promoted to the root. This
 * restores it: same same-origin proxy (/api/fideo-ask), same run, answers
 * computed from Rachel's actual numbers. The chips now ASK rather than copy.
 */
function AskItChat({
  onWantVoice,
  ask,
}: {
  onWantVoice: () => void;
  ask: ShowcaseConfig["ask"];
}) {
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [asked, setAsked] = useState<Set<string>>(new Set());
  // Real ask counts per question for a note (from /api/notes). The chips are
  // re-ordered by popularity once anything has been asked; visitor-typed
  // questions join the list only after 5+ asks (see lib/note-counters.ts).
  const [popular, setPopular] = useState<{ question: string; count: number }[]>([]);
  const threadRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!ask.noteSlug) return;
    fetch(`/api/notes?slug=${encodeURIComponent(ask.noteSlug)}`)
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => d?.questions && setPopular(d.questions))
      .catch(() => {});
  }, [ask.noteSlug]);

  const countOf = (q: string) => popular.find((p) => p.question === q)?.count ?? 0;
  const chips = [
    ...ask.chips,
    ...popular.map((p) => p.question).filter((q) => !ask.chips.includes(q)),
  ].sort((a, b) => countOf(b) - countOf(a));
  const anyAsked = popular.some((p) => p.count > 0);

  // Keep the newest message in view. The pane is short, so this matters more
  // here than it does in the roomier fideo layout.
  useEffect(() => {
    const el = threadRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [messages, loading]);

  async function send(raw: string) {
    const question = raw.trim();
    if (!question || loading) return;
    if (question.length > MAX_QUESTION_LEN) return;

    setInput("");
    setMessages((prev) => [...prev, { role: "user", content: question }]);
    setLoading(true);
    const startedAt = Date.now();

    const holdThinking = async () => {
      const elapsed = Date.now() - startedAt;
      if (elapsed < MIN_THINK_MS) {
        await new Promise((r) => setTimeout(r, MIN_THINK_MS - elapsed));
      }
    };

    if (ask.noteSlug && posthog.__loaded) {
      posthog.capture("note_ask", { note_slug: ask.noteSlug, run_id: ask.runId, question });
    }

    try {
      const res = await fetch("/api/fideo-ask", {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({
          run_id: ask.runId,
          question,
          note_slug: ask.noteSlug,
          is_chip: ask.chips.includes(question),
        }),
      });
      const data = (await res.json().catch(() => ({}))) as {
        answer?: string;
        error?: string;
        rate_limited?: boolean;
      };
      await holdThinking();

      // Never leave a blank state — a failed ask still gets a readable reply
      // in the thread rather than silence.
      const content =
        res.ok && typeof data.answer === "string"
          ? data.answer
          : data.error || "Sorry — I couldn’t answer that. Please try again.";
      setMessages((prev) => [
        ...prev,
        { role: "assistant", content, rateLimited: Boolean(data.rate_limited) },
      ]);
      if (res.ok && ask.noteSlug) {
        setPopular((prev) => {
          const hit = prev.find((p) => p.question === question);
          return hit
            ? prev.map((p) => (p === hit ? { ...p, count: p.count + 1 } : p))
            : [...prev, { question, count: 1 }];
        });
      }
    } catch {
      await holdThinking();
      setMessages((prev) => [
        ...prev,
        {
          role: "assistant",
          content:
            "Something went wrong reaching the assistant. Please try again.",
        },
      ]);
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="askchat">
      <div className="askthread" ref={threadRef}>
        {messages.length === 0 && !loading && (
          <p className="askempty">{ask.emptyCopy}</p>
        )}
        {messages.map((m, i) => (
          <div key={i} className={m.role === "user" ? "bub q" : "bub"}>
            {m.role === "assistant" ? renderAnswer(m.content) : m.content}
            {m.rateLimited && (
              <a className="bublink" href={CALENDLY_URL} target="_blank" rel="noopener noreferrer">
                Book a demo &rarr;
              </a>
            )}
          </div>
        ))}
        {loading && (
          <div className="bub thinking" aria-live="polite">
            <span className="dot" />
            <span className="dot" />
            <span className="dot" />
          </div>
        )}
      </div>

      <div className="asktry">
        <span className="asktry-lbl">
          {anyAsked ? "Most asked on this note" : ASK_CHIPS.label}
        </span>
        <div className="askchips">
          {chips.map((question) => (
            <button
              type="button"
              key={question}
              className={asked.has(question) ? "achip used" : "achip"}
              disabled={loading}
              onClick={() => {
                setAsked((prev) => new Set(prev).add(question));
                send(question);
              }}
            >
              &ldquo;{question}&rdquo;
              {countOf(question) > 0 && (
                <span className="achipn">{countOf(question)}&times;</span>
              )}
            </button>
          ))}
        </div>
      </div>

      <form
        className="askform"
        onSubmit={(e) => {
          e.preventDefault();
          send(input);
        }}
      >
        <input
          className="askinput"
          type="text"
          value={input}
          maxLength={MAX_QUESTION_LEN}
          placeholder={ask.placeholder}
          aria-label={ask.placeholder}
          disabled={loading}
          onChange={(e) => setInput(e.target.value)}
        />
        <button
          type="submit"
          className="asksend"
          disabled={loading || input.trim().length === 0}
        >
          Ask
        </button>
      </form>

      <button type="button" className="askvoice" onClick={onWantVoice}>
        Talk to it instead &rarr;
      </button>
    </div>
  );
}

function AskWidget({ ask }: { ask: ShowcaseConfig["ask"] }) {
  const mountRef = useRef<HTMLDivElement>(null);
  const mountedRef = useRef(false);

  // Create the custom element once the embed script has defined it. Guarded so
  // React strict-mode's dev double-invoke doesn't append it twice.
  const mountWidget = () => {
    if (mountedRef.current || !mountRef.current) return;
    const el = document.createElement("elevenlabs-convai");
    el.setAttribute("agent-id", ASK_AGENT_ID);
    const label = ask.noteSlug ? "Ask this note" : "Ask her portfolio";
    el.setAttribute("action-text", label);
    el.setAttribute("start-call-text", label);
    el.setAttribute("end-call-text", "End the call");
    el.setAttribute("listening-text", "Listening…");
    el.setAttribute("speaking-text", ask.noteSlug ? "Answering…" : "Rachel's portfolio is answering…");
    el.setAttribute("avatar-orb-color-1", "#16324F");
    el.setAttribute("avatar-orb-color-2", "#9C7A3C");
    el.setAttribute(
      "dynamic-variables",
      JSON.stringify({
        run_id: ask.runId,
        video_title: ask.title,
        video_topic: ask.topic,
      })
    );
    mountRef.current.appendChild(el);
    mountedRef.current = true;
  };

  useEffect(() => {
    // If the embed script already ran (e.g. fast refresh), mount right away.
    if (customElements.get("elevenlabs-convai")) mountWidget();
  }, []);

  return (
    <>
      <Script
        src="https://unpkg.com/@elevenlabs/convai-widget-embed"
        strategy="afterInteractive"
        onLoad={mountWidget}
      />
      <div ref={mountRef} className="widgethost" />
    </>
  );
}

const HEAR_AUDIO_SRC =
  "https://oneshot-pipeline-assets.s3.amazonaws.com/kreayt-site/killer_trailer_v7_audio.m4a";
// Memorandum register: unplayed bars = faint hairline; played = gold.
const WAVE_GREY = "#D8D2C4";
const WAVE_GOLD = "#9C7A3C";

// The homepage row exactly as shipped in Round 7.
export const DEFAULT_CONFIG: ShowcaseConfig = {
  video: {
    src: "https://oneshot-pipeline-assets.s3.amazonaws.com/kreayt-site/killer_trailer_v7.mp4",
    poster: "/v2/poster.jpg",
    kicker: "Personal review · 3:10",
    caption: "Rachel Hartman · Q2 2026 · her positions, her numbers",
    ariaLabel: "Play Rachel’s quarterly review",
  },
  ask: {
    runId: ASK_DEMO_RUN.runId,
    title: ASK_DEMO_RUN.title,
    topic: ASK_DEMO_RUN.topic,
    chips: ASK_CHIPS.suggestedQuestions,
    emptyCopy:
      "Ask about Rachel’s quarter. Every answer is computed from her actual positions, not written by a model.",
    placeholder: "Ask her portfolio a question…",
    simNote: ASK_CHIPS.simNote,
  },
  hear: {
    src: HEAR_AUDIO_SRC,
    peaks: RACHEL_AUDIO_PEAKS,
    title: "Rachel’s review — the audio edition",
    kicker: "Same review · for the commute · 3:05",
    line: "Every review ships as an audio edition.",
    simNote: "Simulated data · not investment advice.",
  },
  phone: true,
};

// Opening choreography order and pace: "video → ask → hear → call", ~1s each,
// then a final step settles back on the video. video/ask/podcast/phone map to
// the four doors in order.
const DEMO_SEQUENCE: PaneKey[] = ["video", "ask", "podcast", "phone"];
const DEMO_STEP_MS = 1000;

/**
 * Holds the single "which pane is expanded" state and runs the one-time
 * opening choreography. The choreography is fully skipped under
 * prefers-reduced-motion AND on narrow (mobile) viewports — in both cases the
 * row simply renders with the video open the whole time. Any real interaction
 * calls `open()` (or `cancel()`), which flips a ref checked before every
 * scheduled step, so a genuine hover/click/focus always wins immediately.
 */
function useAccordionChoreography(sequence: PaneKey[] = DEMO_SEQUENCE) {
  const [expanded, setExpanded] = useState<PaneKey>("video");
  const cancelledRef = useRef(false);

  useEffect(() => {
    const reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    const narrow = window.matchMedia("(max-width: 900px)").matches;
    if (reduce || narrow) return; // no choreography; video stays open

    cancelledRef.current = false;
    const timeouts: ReturnType<typeof setTimeout>[] = [];

    sequence.forEach((pane, i) => {
      timeouts.push(
        setTimeout(() => {
          if (cancelledRef.current) return;
          setExpanded(pane);
        }, i * DEMO_STEP_MS)
      );
    });

    // Settle back on the video once the cycle finishes.
    timeouts.push(
      setTimeout(() => {
        if (cancelledRef.current) return;
        setExpanded("video");
      }, sequence.length * DEMO_STEP_MS)
    );

    return () => timeouts.forEach(clearTimeout);
    // The sequence is fixed for the life of the row.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Stops the choreography without changing which pane is open (used for
  // interactions that don't target a specific pane, e.g. a touch on the gap).
  const cancel = () => {
    cancelledRef.current = true;
  };

  // Opens a specific pane and cancels the choreography permanently.
  const open = (pane: PaneKey) => {
    cancelledRef.current = true;
    setExpanded(pane);
  };

  return { expanded, open, cancel };
}

function Pane({
  paneKey,
  title,
  kicker,
  kickerClass,
  expanded,
  onOpen,
  children,
}: {
  paneKey: PaneKey;
  title: string;
  kicker: string;
  kickerClass?: string;
  expanded: PaneKey;
  onOpen: (pane: PaneKey) => void;
  children: React.ReactNode;
}) {
  const isExpanded = expanded === paneKey;
  const requestOpen = () => {
    if (expanded !== paneKey) onOpen(paneKey);
  };
  return (
    <div
      className={`pane ${paneKey} ${isExpanded ? "expanded" : "collapsed"}`}
      onMouseEnter={requestOpen}
      // Backstop for the "cursor already resting on a collapsed pane" case:
      // mouseenter only fires when the pointer crosses INTO the pane, so if
      // the page loads (or the opening choreography settles) with the cursor
      // already over a collapsed door, no mouseenter ever fires and the door
      // wouldn't open until you left and re-entered — which read as "hover
      // needs a click first." mousemove fires on the first motion over the
      // pane, guarded so it's a no-op once this pane is already open.
      onMouseMove={requestOpen}
    >
      <button
        type="button"
        className="panetrigger"
        aria-expanded={isExpanded}
        aria-label={title}
        onClick={() => onOpen(paneKey)}
        onFocus={() => onOpen(paneKey)}
      >
        {/* Vertical strip title — shown only when this pane is collapsed on
            desktop. Marked aria-hidden: the button already carries the title
            via aria-label, so this is a visual duplicate. */}
        <span className="striptitle" aria-hidden="true">
          <span className="stglyph">⤢</span>
          <span className="stt">{title}</span>
        </span>
        {/* Horizontal header — shown when expanded (and, on mobile, always,
            as the tappable bar). */}
        <span className="head">
          <span className="t">{title}</span>
          <span className="headright">
            <span className={kickerClass ? `k ${kickerClass}` : "k"}>{kicker}</span>
            <span className="expand" aria-hidden="true">⤢</span>
          </span>
        </span>
      </button>
      <div className="body">{children}</div>
    </div>
  );
}

/**
 * The "Hear it" audio player + audiogram. The waveform is drawn on a <canvas>
 * from precomputed peaks (RACHEL_AUDIO_PEAKS) — never from a live Web Audio
 * AnalyserNode, which would hit CORS against the S3 file. The played/unplayed
 * fill is driven purely by the audio element's currentTime/duration, repainted
 * via requestAnimationFrame while playing (cheap — one canvas repaint) and on
 * seek/metadata otherwise. Clicking the waveform seeks. Under
 * prefers-reduced-motion the playhead pulse is dropped, but the progress fill
 * still updates (it's information, not decoration). The <canvas> is
 * aria-hidden — the native <audio> controls remain the accessible surface.
 */
function HearItPlayer({
  hear,
  track,
}: {
  hear: ShowcaseConfig["hear"];
  track?: ShowcaseConfig["track"];
}) {
  const audioRef = useRef<HTMLAudioElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const rafRef = useRef<number | null>(null);
  const pendingSeekRef = useRef<number | null>(null);
  const tracking = useMediaTracking(track ? { ...track, media: "audio" } : null);

  useEffect(() => {
    const canvas = canvasRef.current;
    const audio = audioRef.current;
    if (!canvas || !audio) return;
    const reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    const peaks = hear.peaks;
    const n = peaks.length;

    const draw = () => {
      const dpr = window.devicePixelRatio || 1;
      const cssW = canvas.clientWidth;
      const cssH = canvas.clientHeight;
      if (cssW === 0 || cssH === 0) return;
      const dw = Math.round(cssW * dpr);
      const dh = Math.round(cssH * dpr);
      if (canvas.width !== dw || canvas.height !== dh) {
        canvas.width = dw;
        canvas.height = dh;
      }
      const ctx = canvas.getContext("2d");
      if (!ctx) return;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      ctx.clearRect(0, 0, cssW, cssH);

      const slot = cssW / n;
      const gap = Math.max(0.5, slot * 0.3);
      const barW = Math.max(0.75, slot - gap);
      const maxH = cssH - 6;
      const minH = 3;
      const dur = audio.duration;
      const progress =
        isFinite(dur) && dur > 0 ? audio.currentTime / dur : 0;
      const playhead = Math.min(n - 1, Math.floor(progress * n));
      const playing = !audio.paused && !audio.ended;

      for (let i = 0; i < n; i++) {
        let h = Math.max(minH, peaks[i] * maxH);
        if (playing && !reduce && progress > 0 && i === playhead) {
          const pulse = 0.5 + 0.5 * Math.sin(Date.now() / 130);
          h = Math.min(cssH, h * (1 + 0.22 * pulse));
        }
        const x = i * slot + (slot - barW) / 2;
        const y = (cssH - h) / 2;
        ctx.fillStyle = progress > 0 && i <= playhead ? WAVE_GOLD : WAVE_GREY;
        ctx.fillRect(x, y, barW, h);
      }
    };

    const loop = () => {
      draw();
      rafRef.current = requestAnimationFrame(loop);
    };
    const startLoop = () => {
      if (rafRef.current == null) rafRef.current = requestAnimationFrame(loop);
    };
    const stopLoop = () => {
      if (rafRef.current != null) {
        cancelAnimationFrame(rafRef.current);
        rafRef.current = null;
      }
      draw();
    };
    const onMeta = () => {
      if (pendingSeekRef.current != null && isFinite(audio.duration)) {
        audio.currentTime = pendingSeekRef.current * audio.duration;
        pendingSeekRef.current = null;
      }
      draw();
    };

    audio.addEventListener("play", startLoop);
    audio.addEventListener("playing", startLoop);
    audio.addEventListener("pause", stopLoop);
    audio.addEventListener("ended", stopLoop);
    audio.addEventListener("seeked", draw);
    audio.addEventListener("timeupdate", draw);
    audio.addEventListener("loadedmetadata", onMeta);

    const ro = new ResizeObserver(draw);
    ro.observe(canvas);
    draw();

    return () => {
      audio.removeEventListener("play", startLoop);
      audio.removeEventListener("playing", startLoop);
      audio.removeEventListener("pause", stopLoop);
      audio.removeEventListener("ended", stopLoop);
      audio.removeEventListener("seeked", draw);
      audio.removeEventListener("timeupdate", draw);
      audio.removeEventListener("loadedmetadata", onMeta);
      ro.disconnect();
      if (rafRef.current != null) cancelAnimationFrame(rafRef.current);
    };
  }, [hear.peaks]);

  const handleSeek = (e: React.MouseEvent<HTMLCanvasElement>) => {
    const audio = audioRef.current;
    const canvas = canvasRef.current;
    if (!audio || !canvas) return;
    const rect = canvas.getBoundingClientRect();
    const frac = Math.min(1, Math.max(0, (e.clientX - rect.left) / rect.width));
    if (isFinite(audio.duration) && audio.duration > 0) {
      audio.currentTime = frac * audio.duration;
    } else {
      // Not loaded yet (preload="none") — remember the target and force a
      // metadata load; onMeta applies it once duration is known.
      pendingSeekRef.current = frac;
      audio.load();
    }
  };

  return (
    <div className="hearplayer">
      <canvas
        ref={canvasRef}
        className="hearwave"
        aria-hidden="true"
        onClick={handleSeek}
      />
      <audio
        ref={audioRef}
        className="hearaudio"
        controls
        preload="none"
        src={hear.src}
        onPlay={tracking.onPlay}
        onTimeUpdate={tracking.onTimeUpdate}
        onEnded={tracking.onEnded}
      >
        Your browser does not support the audio element.
      </audio>
    </div>
  );
}

/**
 * The "Watch it" video, with the click-to-play affordance Jay asked for.
 *
 * The pane used to render a poster frame under native controls, which on a
 * wide pane reads as a still image — nothing signals that it plays. This puts
 * an unmistakable button in the middle of the frame. It shows before the
 * first play and again after the video ends; it deliberately does NOT come
 * back on a manual pause, which would slap a button over the frame every time
 * someone pauses to read a chart.
 */
function WatchItVideo({
  video,
  track,
}: {
  video: ShowcaseConfig["video"];
  track?: ShowcaseConfig["track"];
}) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [showPlay, setShowPlay] = useState(true);
  const tracking = useMediaTracking(track ? { ...track, media: "video" } : null);

  return (
    <>
      <video
        ref={videoRef}
        playsInline
        preload="metadata"
        controls
        controlsList="nodownload noplaybackrate"
        disablePictureInPicture
        onContextMenu={(e) => e.preventDefault()}
        onPlay={(e) => {
          setShowPlay(false);
          tracking.onPlay();
          void e;
        }}
        onTimeUpdate={tracking.onTimeUpdate}
        onEnded={() => {
          setShowPlay(true);
          tracking.onEnded();
        }}
        poster={video.poster}
      >
        <source src={video.src} type="video/mp4" />
        Your browser does not support the video tag.
      </video>
      {showPlay && (
        <button
          type="button"
          className="playover"
          aria-label={video.ariaLabel}
          onClick={() => {
            const v = videoRef.current;
            if (!v) return;
            // If it ended, rewind so the button restarts rather than no-ops.
            if (v.ended) v.currentTime = 0;
            void v.play();
          }}
        >
          <span className="playtri" aria-hidden="true" />
          <span className="playtxt">Click to play</span>
        </button>
      )}
      <div className="cap">{video.caption}</div>
    </>
  );
}

export function ShowcaseRowV3({ config = DEFAULT_CONFIG }: { config?: ShowcaseConfig }) {
  const sequence: PaneKey[] = config.phone ? DEMO_SEQUENCE : ["video", "ask", "podcast"];
  const { expanded, open, cancel } = useAccordionChoreography(sequence);
  // Mounts the ElevenLabs voice widget, and only ever on an explicit request
  // from the Ask it pane. Once true it stays true for the session so an
  // in-progress conversation survives pane switches.
  const [voiceOn, setVoiceOn] = useState(false);
  const doors = config.phone ? "Four" : "Three";

  return (
    <>
      <div
        className="showcase"
        id="demo-a"
        onMouseEnter={cancel}
        onTouchStart={cancel}
        onFocusCapture={cancel}
      >
        <Pane
          paneKey="video"
          title="Watch it"
          kicker={config.video.kicker}
          expanded={expanded}
          onOpen={open}
        >
          <WatchItVideo video={config.video} track={config.track} />
        </Pane>

        <Pane
          paneKey="ask"
          title="Ask it"
          kicker="Live · computed answers"
          expanded={expanded}
          onOpen={open}
        >
          <AskItChat ask={config.ask} onWantVoice={() => setVoiceOn(true)} />
          {voiceOn && (
            <p className="askcode">
              Demo access code <b>4217</b> — the voice agent is opening
              bottom-right.
            </p>
          )}
          <p className="paneguard">
            Facts only, computed from cited data. Anything needing judgment
            goes to your advisor — it doesn&rsquo;t guess.
          </p>
          {config.ask.simNote && <p className="panesim">{config.ask.simNote}</p>}
        </Pane>

        <Pane
          paneKey="podcast"
          title="Hear it"
          kicker="Audio edition"
          expanded={expanded}
          onOpen={open}
        >
          <div className="hearhead">
            <span className="heart">{config.hear.title}</span>
            <span className="heark">{config.hear.kicker}</span>
          </div>
          <HearItPlayer hear={config.hear} track={config.track} />
          <p className="hearline">{config.hear.line}</p>
          {config.hear.simNote && <p className="panesim">{config.hear.simNote}</p>}
        </Pane>

        {config.phone && (
          <Pane
            paneKey="phone"
            title="Call it"
            kicker="Always on"
            expanded={expanded}
            onOpen={open}
          >
            <div className="phbig">It picks up the phone.</div>
            <div className="phguard">
              Facts only — anything that needs judgment, she puts you on with
              your advisor.
            </div>
            <a className="phnum" href="tel:+14472244665">
              <div className="n">+1 (447) 224-4665</div>
              <div className="c">Access code = her key</div>
            </a>
          </Pane>
        )}
      </div>
      <p className="showhint">
        <span className="showhint-desktop">{doors} doors — open any one</span>
        <span className="showhint-touch">{doors} doors — tap any one</span>
      </p>
      {/* Rendered at the row root (not inside the ask pane) so the widget's
          floating orb survives pane collapse — the ask pane's body is
          opacity:0 / display:none when another pane is open. Mounts only
          when a visitor explicitly asks for voice; stays mounted for the
          session. */}
      {voiceOn && <AskWidget ask={config.ask} />}
    </>
  );
}
